import { useCallback, useMemo } from 'react';

import { Card, IAppboy } from 'appboy-web-sdk';

import useReadyQueue from 'hooks/use-ready-queue';

type ToggleContentCards = <E extends Element>(p: E) => void;

export interface IBrazeContext {
  initBraze: () => void;
  getCachedContentCards(): Promise<Card[]>;
  setUserId(userId: string | null): void;
  toggleContentCards: ToggleContentCards;
}

const useBraze = () => {
  const { enqueueIfNotDrained, drainQueue } = useReadyQueue();

  const toggleContentCards = useCallback<ToggleContentCards>(
    enqueueIfNotDrained(parentNode => {
      window.appboy.display.toggleContentCards(parentNode);
    }),
    [enqueueIfNotDrained]
  );

  const setUserId = useCallback(
    enqueueIfNotDrained((userId: string | null) => {
      window.appboy.changeUser(userId);
      return window.appboy.requestContentCardsRefresh();
    }),
    [enqueueIfNotDrained]
  );

  // Making this async to be compatible with the react native module
  const getCachedContentCards = useCallback(async () => {
    if (!window.appboy) {
      return [];
    }
    return window.appboy.getCachedContentCards()?.cards ?? [];
  }, []);

  const initBraze = () => {
    if (window.appboy?.initialize()) {
      window.appboy.display.automaticallyShowNewInAppMessages();
      window.appboy.openSession();
      drainQueue();
      window.appboy.requestImmediateDataFlush();
    }
  };

  const ctxValue: IBrazeContext = useMemo(
    () => ({
      initBraze,
      getCachedContentCards,
      setUserId,
      toggleContentCards,
    }),
    [initBraze, getCachedContentCards, setUserId, toggleContentCards]
  );

  return ctxValue;
};

export default useBraze;

declare global {
  interface Window {
    appboy: IAppboy;
  }
}
